/**
 * Bundle 164 A2 - Broken Names page.
 *
 * Route: /admin/users/broken-names
 * Reuses the existing `/admin/health/broken-names/rows`
 * drill-down endpoint (Bundle 46 Task 2). Adds per-row
 * [Open profile] and a "Repair name split" bulk action
 * shortcut that reuses the primary_action endpoint from the
 * health card.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserX, ExternalLink, Wrench, RefreshCw } from 'lucide-react';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';

const AdminBrokenNamesPage = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [repairBusy, setRepairBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        '/admin/health/broken-names/rows');
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to load rows.'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doRepairAll = async () => {
    if (!window.confirm(
      `Repair name split for all ${count} affected users? This runs the`
      + ' Bundle 45 name-split repair over every row in this list.')) {
      return;
    }
    setRepairBusy(true);
    try {
      const { data } = await api.post(
        '/admin/maintenance/repair-name-split?dry_run=false');
      toast.success(
        `Repair complete: ${data?.repaired ?? '?'} row(s) fixed.`);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Repair failed.'));
    } finally {
      setRepairBusy(false);
    }
  };

  const doRepairOne = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/repair-all`);
      toast.success('User repaired.');
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Repair failed.'));
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto p-6 space-y-4"
      data-testid="admin-broken-names-page"
    >
      <div className="flex items-center gap-2">
        <UserX className="h-5 w-5 text-primary-800" />
        <h1 className="text-2xl font-semibold text-primary-900">
          Broken names
        </h1>
        <Badge variant="outline" data-testid="admin-broken-names-count">
          {count} total
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="admin-broken-names-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
          <Button
            size="sm"
            className="bg-primary-900 text-white hover:bg-primary-800"
            onClick={doRepairAll}
            disabled={repairBusy || !count}
            data-testid="admin-broken-names-repair-all"
          >
            <Wrench className="h-3.5 w-3.5 mr-1" />
            Repair all
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Users whose edit form renders a blank first / last name
          </CardTitle>
          <div className="text-xs text-neutral-600">
            Root-level `first_name` / `last_name` is empty. Usually
            caused by the initial sign-up placeholder or the older
            RSVP hydrator.
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="p-2 text-left">Display name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Problem</th>
                  <th className="p-2 text-left">Created</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.user_id}
                    className="border-t border-neutral-100"
                    data-testid={`bn-row-${r.user_id}`}
                  >
                    <td className="p-2 font-medium text-primary-900">
                      {r.display_name}
                    </td>
                    <td className="p-2 font-mono text-xs text-neutral-700">
                      {r.email}
                    </td>
                    <td className="p-2 text-xs text-amber-800">
                      {r.problem}
                    </td>
                    <td className="p-2 text-xs text-neutral-500 whitespace-nowrap">
                      {(r.created_at || '').slice(0, 10)}
                    </td>
                    <td className="p-2 space-x-1 whitespace-nowrap">
                      <Link
                        to={`/admin/users/${r.user_id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-[10px] font-medium text-primary-900 hover:border-primary-400"
                        data-testid={`bn-open-${r.user_id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open profile
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => doRepairOne(r.user_id)}
                        data-testid={`bn-repair-${r.user_id}`}
                      >
                        Repair
                      </Button>
                    </td>
                  </tr>
                ))}
                {!rows.length && !loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-6 text-center text-neutral-400"
                      data-testid="admin-broken-names-empty"
                    >
                      No broken names. Everything looks populated.
                    </td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-6 text-center text-neutral-400"
                    >
                      Loading.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBrokenNamesPage;
